import { useState } from 'react';
import { Image, Plus, X, Check, Trash2 } from 'lucide-react';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';

export default function AdminAlbums({ albums, onRefresh }) {
  const [editingAlbum, setEditingAlbum] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [newAlbum, setNewAlbum] = useState({
    title: '', cover_url: '', description: '', price: 12.99, release_date: new Date().toISOString().split('T')[0]
  });

  const createAlbum = async () => {
    if (!newAlbum.title) {
      toast.error('Title is required');
      return;
    }
    try {
      await axios.post(`${API_URL}/admin/albums`, newAlbum);
      toast.success('Album created!');
      setShowCreate(false);
      setNewAlbum({ title: '', cover_url: '', description: '', price: 12.99, release_date: new Date().toISOString().split('T')[0] });
      onRefresh();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create album');
    }
  };

  const saveAlbum = async (album) => {
    try {
      await axios.put(`${API_URL}/admin/albums/${album.id}`, {
        title: album.title,
        cover_url: album.cover_url,
        description: album.description,
        price: album.price,
        release_date: album.release_date
      });
      toast.success('Album updated');
      setEditingAlbum(null);
      onRefresh();
    } catch (error) {
      toast.error('Failed to update album');
    }
  };

  const deleteAlbum = async (id) => {
    if (!window.confirm('Delete this album?')) return;
    try {
      await axios.delete(`${API_URL}/admin/albums/${id}`);
      toast.success('Album deleted');
      onRefresh();
    } catch (error) {
      toast.error('Failed to delete album');
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center mb-4">
        <h2 className="text-xl font-bold">Manage Albums</h2>
        <button onClick={() => setShowCreate(true)} className="btn-primary" data-testid="create-album-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Album
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <div className="glass-card p-6 mb-4">
          <h3 className="text-lg font-semibold mb-4">Create New Album</h3>
          <div className="grid md:grid-cols-2 gap-4">
            <div>
              <Label>Title</Label>
              <Input value={newAlbum.title} onChange={(e) => setNewAlbum({...newAlbum, title: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Price ($)</Label>
              <Input type="number" step="0.01" value={newAlbum.price} onChange={(e) => setNewAlbum({...newAlbum, price: parseFloat(e.target.value)})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Release Date</Label>
              <Input type="date" value={newAlbum.release_date} onChange={(e) => setNewAlbum({...newAlbum, release_date: e.target.value})} className="input-dark mt-1" />
            </div>
            <div>
              <Label>Cover URL</Label>
              <Input value={newAlbum.cover_url} onChange={(e) => setNewAlbum({...newAlbum, cover_url: e.target.value})} className="input-dark mt-1" />
            </div>
            <div className="md:col-span-2">
              <Label>Description</Label>
              <Input value={newAlbum.description} onChange={(e) => setNewAlbum({...newAlbum, description: e.target.value})} className="input-dark mt-1" />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <button onClick={() => setShowCreate(false)} className="btn-ghost">Cancel</button>
            <button onClick={createAlbum} className="btn-primary"><Plus className="w-4 h-4 mr-2" /> Create</button>
          </div>
        </div>
      )}

      {/* Album List */}
      {albums.length === 0 ? (
        <div className="glass-card p-8 text-center">
          <Image className="w-12 h-12 text-gray-600 mx-auto mb-4" />
          <h3 className="text-lg font-semibold mb-2">No Albums Yet</h3>
          <p className="text-gray-400 mb-4">Create your first album</p>
        </div>
      ) : (
        albums.map(album => (
          <div key={album.id} className="glass-card p-4" data-testid={`album-item-${album.id}`}>
            {editingAlbum?.id === album.id ? (
              <div className="space-y-4">
                <div className="grid md:grid-cols-3 gap-4">
                  <div><Label>Title</Label><Input value={editingAlbum.title} onChange={(e) => setEditingAlbum({...editingAlbum, title: e.target.value})} className="input-dark mt-1" /></div>
                  <div><Label>Price</Label><Input type="number" step="0.01" value={editingAlbum.price} onChange={(e) => setEditingAlbum({...editingAlbum, price: parseFloat(e.target.value)})} className="input-dark mt-1" /></div>
                  <div><Label>Cover URL</Label><Input value={editingAlbum.cover_url} onChange={(e) => setEditingAlbum({...editingAlbum, cover_url: e.target.value})} className="input-dark mt-1" /></div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditingAlbum(null)} className="btn-ghost"><X className="w-4 h-4" /></button>
                  <button onClick={() => saveAlbum(editingAlbum)} className="btn-primary"><Check className="w-4 h-4" /></button>
                </div>
              </div>
            ) : (
              <div className="flex items-center gap-4">
                <img src={album.cover_url || '/placeholder-album.png'} alt={album.title} className="w-16 h-16 rounded-lg object-cover" />
                <div className="flex-1 min-w-0">
                  <h3 className="font-semibold truncate">{album.title}</h3>
                  <p className="text-sm text-gray-400">{album.description || 'No description'}</p>
                </div>
                <div className="text-right mr-4">
                  <p className="font-bold">${album.price?.toFixed(2)}</p>
                  <p className="text-xs text-gray-500">{album.release_date}</p>
                </div>
                <div className="flex gap-2">
                  <button onClick={() => setEditingAlbum({...album})} className="btn-ghost">Edit</button>
                  <button onClick={() => deleteAlbum(album.id)} className="btn-ghost text-red-400"><Trash2 className="w-4 h-4" /></button>
                </div>
              </div>
            )}
          </div>
        ))
      )}
    </div>
  );
}
